import React from 'react';
import { AlertTriangleIcon, UserSearchIcon, XCircleIcon } from 'lucide-react';
import type { ESignPackage } from '../types/docmagic';
import type { BusyAction } from '../hooks/usePackages';
import { formatDateTime } from '../utils/datetime';
import { allRequiredComplete } from '../utils/packageStatus';
interface PackageAlertsProps {
  pkg: ESignPackage;
  busy?: BusyAction;
  onSync: () => void;
  onRetryPdf: () => void;
}
export function PackageAlerts({
  pkg,
  busy,
  onSync,
  onRetryPdf
}: PackageAlertsProps) {
  const pdfPending = allRequiredComplete(pkg) && pkg.pdfStatus !== 'Stored';
  const hasAlerts = pkg.syncStatus === 'Failed' || pkg.reviewAlert || pdfPending;
  if (!hasAlerts) return null;
  return <div className="space-y-2 px-4 pt-4" data-id="element-86" data-mp="oypiy2bva6cs">
      {pkg.syncStatus === 'Failed' && <div role="alert" className="flex flex-wrap items-start gap-3 rounded border border-[#f5b8b8] bg-[#fdeded] px-3 py-2.5" data-id="element-87" data-mp="axmytv4fn64i">
          <XCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-error" aria-hidden="true" data-id="element-88" data-mp="eyyt4dy94fj1" />
          <div className="min-w-0 flex-1 text-sm" data-id="element-89" data-mp="oved38gspc4q">
            <p className="font-semibold text-error" data-id="element-90" data-mp="jxuwi1wmkn9m">Sync failed — DocMagic could not be reached</p>
            <p className="text-ink" data-id="element-91" data-mp="yulmxlobmon3">{pkg.syncError} Showing last known data from {formatDateTime(pkg.lastSyncedAt)}.</p>
          </div>
          <button type="button" onClick={onSync} disabled={busy === 'sync'} className="rounded bg-error px-3 py-1.5 text-sm font-medium text-white hover:bg-[#8e030f] disabled:cursor-wait disabled:opacity-70" data-id="element-92" data-mp="twtwyx78lclj">
            {busy === 'sync' ? 'Retrying…' : 'Retry sync'}
          </button>
        </div>}

      {pkg.reviewAlert && <div role="status" className="flex items-start gap-3 rounded border border-[#f7d59c] bg-[#fef5e6] px-3 py-2.5" data-id="element-93" data-mp="b49gygcmz1z5">
          <UserSearchIcon className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden="true" data-id="element-94" data-mp="3jjjgbgp55t38" />
          <div className="text-sm" data-id="element-95" data-mp="3elhw2sfzbnq6">
            <p className="font-semibold text-warning" data-id="element-96" data-mp="84r5nh2wuvhu">No unique borrower match — sent to {pkg.reviewAlert.assignee} for manual review</p>
            <p className="text-ink" data-id="element-97" data-mp="c5b18g7u5r1v">
              {pkg.reviewAlert.reason} Alert created {formatDateTime(pkg.reviewAlert.createdAt)}. The package is retained
              here until it's linked to a loan.
            </p>
          </div>
        </div>}

      {pdfPending && <div role="status" className="flex flex-wrap items-start gap-3 rounded border border-[#f7d59c] bg-[#fef5e6] px-3 py-2.5" data-id="element-98" data-mp="3lbsueomu5mah">
          <AlertTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden="true" data-id="element-99" data-mp="3pdnwhvclc3pb" />
          <div className="min-w-0 flex-1 text-sm" data-id="element-100" data-mp="3ub7mttmkc6xm">
            <p className="font-semibold text-warning" data-id="element-101" data-mp="35ej6xfybqfs">Completed — signed PDF not yet stored</p>
            <p className="text-ink" data-id="element-102" data-mp="19lqk36a5rmf2">{pkg.pdfError ?? 'All required borrowers have signed. The signed package is waiting to be retrieved.'}</p>
          </div>
          <button type="button" onClick={onRetryPdf} disabled={busy === 'pdf'} className="rounded bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-dark disabled:cursor-wait disabled:opacity-70" data-id="element-103" data-mp="1ej9zi9ile58l">
            {busy === 'pdf' ? 'Retrieving…' : 'Retry PDF retrieval'}
          </button>
        </div>}
    </div>;
}